import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, SortAsc, SortDesc, Calendar, Filter, X, Plus } from 'lucide-react';
import { ChevronUp, EyeOff, Eye } from 'lucide-react';
import Header from './Header';
import { getUserInfo } from './apiUtils';
import UpdateClassificationDialog from './UpdateClassificationDialog';
import CopyRequirementDialog from './CopyRequirementDialog';
import ManageApproversDialog from './ManageApproversDialog';
import ApproveRequirementsDialog from './ApproveRequirementsDialog';
import ManageUseCasesDialog from './ManageUseCasesDialog';
import ManageCommentsDialog from './ManageCommentsDialog';
import ManageLinksDialog from './ManageLinksDialog';
import ManageRequirementExceptions from './ManageRequirementExceptions';
import CreateExceptionDialog from './CreateExceptionDialog';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

// Styles for dialog boxes
const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '700px',
    maxWidth: '90vw',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    maxHeight: '90vh',
    overflowY: 'auto'
  }
};

const MyRequirementApprovals = () => {
  // State for requirements data
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
const startIndex = (currentPage - 1) * itemsPerPage;

  // State for filters
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [approverFilter, setApproverFilter] = useState([]);
  const [criticalityFilter, setCriticalityFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);

  // State for date filters
  const [createdDateFilter, setCreatedDateFilter] = useState({ startDate: '', endDate: '' });
  const [updatedDateFilter, setUpdatedDateFilter] = useState({ startDate: '', endDate: '' });


// Add this with other filter states
const [approvalStatusFilter, setApprovalStatusFilter] = useState([]);
const [approvalDateFilter, setApprovalDateFilter] = useState({ startDate: '', endDate: '' });

// Add this with other available options states
const [availableApprovalStatuses, setAvailableApprovalStatuses] = useState([]);

  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: 'req_created_date', direction: 'desc' });

  // State for available filter options
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableApprovers, setAvailableApprovers] = useState([]);
  const [availableCriticalities, setAvailableCriticalities] = useState([]);
  const [availablePriorities, setAvailablePriorities] = useState([]);


const [showFilters, setShowFilters] = useState(false);

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;


  const [allStatuses, setAllStatuses] = useState([]);
  const [allApprovers, setAllApprovers] = useState([]);
  const [allCriticalities, setAllCriticalities] = useState([]);
  const [allPriorities, setAllPriorities] = useState([]);
  const [allApprovalStatuses, setAllApprovalStatuses] = useState([]);


  // State for selected requirements
  const [selectedRequirements, setSelectedRequirements] = useState(new Set());

  // State for dialogs
  const [isUpdateStatusDialogOpen, setIsUpdateStatusDialogOpen] = useState(false);
  const [isUpdateCriticalityDialogOpen, setIsUpdateCriticalityDialogOpen] = useState(false);
  const [isUpdatePriorityDialogOpen, setIsUpdatePriorityDialogOpen] = useState(false);
  const [isClassificationDialogOpen, setIsClassificationDialogOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isManageCommentsDialogOpen, setIsManageCommentsDialogOpen] = useState(false);
  const [isManageLinksDialogOpen, setIsManageLinksDialogOpen] = useState(false);
  const [isKeyAttributesDialogOpen, setIsKeyAttributesDialogOpen] = useState(false);
  const [isCreateExceptionDialogOpen, setIsCreateExceptionDialogOpen] = useState(false);
  const [isManageApproversDialogOpen, setIsManageApproversDialogOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [isManageUseCasesDialogOpen, setIsManageUseCasesDialogOpen] = useState(false);

  // State for requirement editing
  const [selectedRequirementForCopy, setSelectedRequirementForCopy] = useState(null);
  const [exceptionLevel, setExceptionLevel] = useState('Requirement');

  // Dialog state for messages
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');


  // Auth and user info state
  const login_token = localStorage.getItem('authToken');
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');
  const [redirectPath, setRedirectPath] = useState('');

  // Permissions state
  const [permissions, setPermissions] = useState({
    canUpdateStatus: false,
    canUpdateCriticality: false,
    canUpdatePriority: false,
    canApprove: false
  });





// Fetch approver list from API





const RequirementTypeBadge = ({ reqType }) => {
  let bgColor = "bg-gray-100";
  let textColor = "text-gray-800";
  let borderColor = "border-gray-300";

  // Normalize the requirement type for comparison
  const normalizedType = (reqType || 'REQUIREMENT').toUpperCase();

  switch (normalizedType) {
    case "PROJECT LEVEL":
      bgColor = "bg-purple-100";
      textColor = "text-purple-800";
      borderColor = "border-purple-300";
      break;
    case "FUNCTIONAL LEVEL":
      bgColor = "bg-blue-100";
      textColor = "text-blue-800";
      borderColor = "border-blue-300";
      break;
    case "INTEGRATION REQUIREMENT":
      bgColor = "bg-orange-100";
      textColor = "text-orange-800";
      borderColor = "border-orange-300";
      break;
    case "REQUIREMENT":
    default:
      bgColor = "bg-green-100";
      textColor = "text-green-800";
      borderColor = "border-green-300";
      break;
  }

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${bgColor} ${textColor} ${borderColor}`}>
      {reqType || 'REQUIREMENT'}
    </span>
  );
};

const fetchApproverList = async () => {
  try {
    const response = await fetch(appConfig.getUrl('/get_approver_list'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject
      })
    });

    const data = await response.json();
    console.log('Fetched approver list:', data);

    if (data.status === 'Success') {
      // Store the complete approver list from the dedicated API
      const masterApproverList = data.approver_list || [];

      // Set this as the master list - this won't change during filtering
      setAllApprovers(masterApproverList);

      // Initially, make all approvers available
      setAvailableApprovers(masterApproverList);

      console.log('Current user ID - 1:', userId);

      // Set current user as default selected approver
      if (userId && masterApproverList.length > 0) {
        const currentUserInList = masterApproverList.find(approver =>
          approver.approval_user_id === userId
        );

        console.log('Current user in approver list:', currentUserInList);
        if (currentUserInList) {
          setApproverFilter([currentUserInList.approval_user_id]);
        }
      }
    } else {
      console.error('Failed to fetch approver list:', data.status_description);
    }
  } catch (error) {
    console.error('Error fetching approver list:', error);
  }
};






// Add these useEffect hooks for dropdown management to MyRequirementApprovals component
// Place these after your other useEffect hooks:

useEffect(() => {
  const handleClickOutside = (event) => {
    // Handle all filter dropdowns
    const dropdowns = [
      'status-filter-dropdown',
      'approver-filter-dropdown',
      'criticality-filter-dropdown',
      'priority-filter-dropdown',
      'approval-status-filter-dropdown'
    ];

    dropdowns.forEach(dropdownId => {
      const dropdown = document.getElementById(dropdownId);
      if (dropdown && dropdown.style.display === 'block') {
        const isClickInside = dropdown.contains(event.target);
        const toggleButton = dropdown.previousElementSibling;
        const isClickOnToggle = toggleButton && toggleButton.contains(event.target);

        if (!isClickInside && !isClickOnToggle) {
          dropdown.style.display = 'none';
        }
      }
    });
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, []);

useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      // Close all filter dropdowns on escape
      const dropdowns = [
        'status-filter-dropdown',
        'approver-filter-dropdown',
        'criticality-filter-dropdown',
        'priority-filter-dropdown',
        'approval-status-filter-dropdown'
      ];

      dropdowns.forEach(dropdownId => {
        const dropdown = document.getElementById(dropdownId);
        if (dropdown && dropdown.style.display === 'block') {
          dropdown.style.display = 'none';
        }
      });
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, []);




useEffect(() => {
  if (corporateAccount && currentProject) {
    fetchAllFilterOptions();
        fetchApproverList();

    checkPermission('Requirements Management');
  }
}, [corporateAccount, currentProject]);



const checkPermission = async (categoryHeader) => {
console.log('Checking permission for:', categoryHeader);
  try {
    const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        user_id: userId,
        project_id: currentProject,
        category_header: categoryHeader,
        category_sub_header: []
      }),
    });

    const data = await response.json();

    // Map API response to permission fields
    data.user_actions_list.forEach(item => {
      switch(item.user_action) {
        case 'Approve Requirements':
          permissions.canApprove = item.has_access;
          break;
        case 'Copy Requirement':
          permissions.canCopy = item.has_access;
          break;
        case 'Create Or Edit Requirement':
          permissions.canCreateRequirement = item.has_access;
          break;
        case 'Update Product Classification':
          permissions.canUpdateClassification = item.has_access;
          break;
        case 'Update Criticality':
          permissions.canUpdateCriticality = item.has_access;
          break;
        case 'Update Priority':
          permissions.canUpdatePriority = item.has_access;
          break;
        case 'Update Status':
          permissions.canUpdateStatus = item.has_access;
          break;
      }
    });

    console.log(`New access permissions data: `, data);
    console.log('New Permissions:', permissions);

    return data.access_status && data.status === 'Success';
  } catch (error) {
    console.error(`Error checking permission for ${categoryHeader}:`, error);
    return false;
  }
};

// Fetch all filter options
// Fetch all filter options
const fetchAllFilterOptions = async () => {

console.log("hello 1")
  try {
    // Fetch statuses
    const statusesResponse = await fetch(appConfig.getUrl('/get_statuses'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        entity: 'REQUIREMENT',
        search_query: ""
      })
    });

    const statusesData = await statusesResponse.json();
    if (statusesData.status === 'Success') {
      const statuses = statusesData.entity_statuses.map(s => s.status);
      setAllStatuses(statuses);

      console.log('All statuses:', statuses);
//      setStatusFilter(statuses); // Set all statuses as default
    }

console.log("hello 2")

// Add this in the fetchAllFilterOptions function
// Fetch approval statuses
const approvalStatusesResponse = await fetch(appConfig.getUrl('/get_statuses'), {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${login_token}`
  },
  body: JSON.stringify({
    corporate_account: corporateAccount,
    project_id: currentProject,
    entity: 'REQUIREMENT_APPROVAL',
    search_query: ""
  })
});

console.log("hello 3")

const approvalStatusesData = await approvalStatusesResponse.json();

console.log('Approval statuses data:', approvalStatusesData);

if (approvalStatusesData.status === 'Success') {
  const approvalStatuses = approvalStatusesData.entity_statuses.map(s => s.status);
  setAllApprovalStatuses(approvalStatuses);
}

    // Fetch criticalities
    const criticalitiesResponse = await fetch(appConfig.getUrl('/get_statuses'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        entity: 'REQUIREMENT_CRITICALITY',
        search_query: ""
      })
    });

    const criticalitiesData = await criticalitiesResponse.json();
    if (criticalitiesData.status === 'Success') {
      const criticalities = criticalitiesData.entity_statuses.map(c => c.status);
      setAllCriticalities(criticalities);
    }

    // Fetch priorities
    const prioritiesResponse = await fetch(appConfig.getUrl('/get_statuses'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        entity: 'REQUIREMENT_PRIORITY',
        search_query: ""
      })
    });

    const prioritiesData = await prioritiesResponse.json();
    if (prioritiesData.status === 'Success') {
      const priorities = prioritiesData.entity_statuses.map(p => p.status);
      setAllPriorities(priorities);
    }

    // Fetch approvers from the dedicated API
//    await fetchApproverList();
  } catch (error) {
    console.error('Error fetching filter options:', error);
  }
};

// Fetch requirements




const fetchRequirements = async () => {
  console.log("permissions are.....", permissions)

  setLoading(true);
  try {
    const response = await fetch(appConfig.getUrl('/get_requirements_by_approver'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        filter_by_status: statusFilter.length > 0 ? statusFilter : null,
        filter_by_approvers: approverFilter.length > 0 ? approverFilter : (userId ? [userId] : null),
        search_query: searchFilter,
        sort_criteria: `${sortConfig.key} ${sortConfig.direction}`,
        requirement_criticality: criticalityFilter.length > 0 ? criticalityFilter : null,
        requirement_priority: priorityFilter.length > 0 ? priorityFilter : null,
        created_date_start: createdDateFilter.startDate,
        created_date_end: createdDateFilter.endDate,
        updated_date_start: updatedDateFilter.startDate,
        updated_date_end: updatedDateFilter.endDate,
        filter_by_approval_status: approvalStatusFilter.length > 0 ? approvalStatusFilter : null,
        approval_date_start: approvalDateFilter.startDate,
        approval_date_end: approvalDateFilter.endDate,
        user_timezone: userTimezone
      })
    });

    const data = await response.json();
    console.log('Fetched requirements:', data);

    if (data.status === 'Success') {
      // Always set requirements to the returned data, even if it's an empty array
      setRequirements(data.requirement_list || []);

      // Extract available filter options from the data
      if (data.requirement_list && data.requirement_list.length > 0) {
        // Get unique values for each filter category
        const statuses = [...new Set(data.requirement_list.map(item => item.req_status))].filter(Boolean);
        const criticalities = [...new Set(data.requirement_list.map(item => item.req_criticality))].filter(Boolean);
        const priorities = [...new Set(data.requirement_list.map(item => item.req_priority))].filter(Boolean);
        const approvalstatuses = [...new Set(data.requirement_list.map(item => item.approval_status))].filter(Boolean);

        // Update these filter options (they can change based on filtered results)
        setAvailableStatuses(statuses);
        setAvailableCriticalities(criticalities);
        setAvailablePriorities(priorities);
        setAvailableApprovalStatuses(approvalstatuses);

        // ONLY update availableApprovers if we don't have a complete list yet from fetchApproverList
        // This prevents losing approvers when they're filtered out of results
        if (availableApprovers.length === 0) {
          const currentApprovers = data.requirement_list
            .map(item => ({
              approval_user_id: item.approver_user_id,
              user_name: item.approver_user_name || item.approver_user_id
            }))
            .filter(approver => approver.approval_user_id);

          // Remove duplicates from current data
          const uniqueCurrentApprovers = currentApprovers.filter((approver, index, self) =>
            index === self.findIndex(a => a.approval_user_id === approver.approval_user_id)
          );

          setAvailableApprovers(uniqueCurrentApprovers);
        }
      } else {
        // If no data, clear other filter options but KEEP approvers
        setAvailableStatuses([]);
        setAvailableCriticalities([]);
        setAvailablePriorities([]);
        setAvailableApprovalStatuses([]);
        // Don't clear availableApprovers - keep the complete list
      }
    } else {
      // Clear the requirements list when there's an error
      setRequirements([]);
      setError(data.status_description || 'Failed to fetch requirements');
      setDialogMessage(data.status_description || 'Failed to fetch requirements');
    }
  } catch (error) {
    // Clear the requirements list when there's an error
    setRequirements([]);
    console.error('Error fetching requirements:', error);
    setError('Error connecting to the server');
    setDialogMessage('Error connecting to the server');
    setShowDialog(true);
  } finally {
    setLoading(false);
  }
};










// Show error dialog
const showErrorDialog = (message, path) => {
  setDialogMessage(message);
  setRedirectPath(path);
  setShowDialog(true);
};

// Get the requirement ID with prefix for the selected requirement
const getSelectedRequirementIdWithPrefix = () => {
  if (selectedRequirements.size === 1) {
    const reqId = Array.from(selectedRequirements)[0];
    const requirement = requirements.find(r => r.req_id === reqId);
    return requirement ? requirement.req_id_with_prefix : '';
  }
  return '';
};

// Get the functional level description for the selected requirement
const getCurrentFunctionalLevelDescription = () => {
  if (selectedRequirements.size === 1) {
    const reqId = Array.from(selectedRequirements)[0];
    const requirement = requirements.find(r => r.req_id === reqId);
    return requirement ? requirement.level_description : '';
  }
  return '';
};

// Handle copy button click
const handleCopyButtonClick = () => {
  if (selectedRequirements.size !== 1) {
    setDialogMessage('Please select exactly one requirement to copy');
    setShowDialog(true);
    return;
  }

  const requirementId = Array.from(selectedRequirements)[0];
  const requirement = requirements.find(req => req.req_id === requirementId);

  if (requirement) {
    setSelectedRequirementForCopy(requirement);
    setIsCopyDialogOpen(true);
  } else {
    setDialogMessage('Selected requirement not found');
    setShowDialog(true);
  }
};

// Handle manage comments click
const handleManageCommentsClick = () => {
  setIsManageCommentsDialogOpen(true);
};

// Handle manage links click
const handleManageLinksClick = () => {
  if (selectedRequirements.size !== 1) {
    setDialogMessage('Please select exactly one requirement to manage links');
    setShowDialog(true);
    return;
  }

  setIsManageLinksDialogOpen(true);
};

// Handle manage use cases click
const handleManageUseCasesClick = () => {
  setIsManageUseCasesDialogOpen(true);
};

// Handle approve button click
const handleApproveButtonClick = () => {
  setIsApprovalDialogOpen(true);
};

// Handle manage approvers click
const handleManageApproversClick = () => {
console.log('Manage Approvers clicked');
  setIsManageApproversDialogOpen(true);
};

// Update status
// Update status
const handleUpdateStatus = async (newStatus) => {
  try {
    // Map REQUIREMENT_APPROVER_RECORD_ID to req_id
    const reqIds = Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean);

    console.log('Mapped req_ids for status update:', reqIds);

    const response = await fetch(appConfig.getUrl('/update_requirement_status'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        req_ids: reqIds, // Use the mapped req_ids instead of selectedRequirements
        status: newStatus
      })
    });

    const data = await response.json();
    if (data.status === 'Success') {
//      setDialogMessage('Requirement status updated successfully');
//      setShowDialog(true);
      setIsUpdateStatusDialogOpen(false);
      await fetchRequirements();
      setSelectedRequirements(new Set());
    } else {
      throw new Error(data.status_description || 'Failed to update status');
    }
  } catch (error) {
    setDialogMessage(error.message);
    setShowDialog(true);
  }
};

// Update criticality
const handleUpdateCriticality = async (newCriticality) => {
  try {
    // Map REQUIREMENT_APPROVER_RECORD_ID to req_id
    const reqIds = Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean);

    console.log('Mapped req_ids for criticality update:', reqIds);

    const response = await fetch(appConfig.getUrl('/update_requirement_criticality'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        req_ids: reqIds, // Use the mapped req_ids instead of selectedRequirements
        req_criticality: newCriticality
      })
    });

    const data = await response.json();
    if (data.status === 'Success') {
//      setDialogMessage('Requirement criticality updated successfully');
//      setShowDialog(true);
      setIsUpdateCriticalityDialogOpen(false);
      await fetchRequirements();
      setSelectedRequirements(new Set());
    } else {
      throw new Error(data.status_description || 'Failed to update criticality');
    }
  } catch (error) {
    setDialogMessage(error.message);
    setShowDialog(true);
  }
};

// Update priority
const handleUpdatePriority = async (newPriority) => {
  try {
    // Map REQUIREMENT_APPROVER_RECORD_ID to req_id
    const reqIds = Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean);

    console.log('Mapped req_ids for priority update:', reqIds);

    const response = await fetch(appConfig.getUrl('/update_requirement_priority'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        req_ids: reqIds, // Use the mapped req_ids instead of selectedRequirements
        req_priority: newPriority
      })
    });

    const data = await response.json();
    if (data.status === 'Success') {
//      setDialogMessage('Requirement priority updated successfully');
//      setShowDialog(true);
      setIsUpdatePriorityDialogOpen(false);
      await fetchRequirements();
      setSelectedRequirements(new Set());
    } else {
      throw new Error(data.status_description || 'Failed to update priority');
    }
  } catch (error) {
    setDialogMessage(error.message);
    setShowDialog(true);
  }
};

// Handle sort
const handleSort = (key) => {
  setSortConfig(prevConfig => ({
    key,
    direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
  }));
};

// Handle search
const handleSearch = () => {
  setSearchFilter(searchInput);
};











// Enhanced Date range picker component
const DateRangePicker = ({ label, value, onChange, icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localDateRange, setLocalDateRange] = useState(value);
  const dropdownRef = useRef(null);

  // Update local state when props change
  useEffect(() => {
    setLocalDateRange(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle quick selections
  const handleQuickSelect = (option) => {
    const today = new Date();
    let startDate = '';
    let endDate = today.toISOString().split('T')[0]; // Today in YYYY-MM-DD format

    switch(option) {
      case 'today':
        startDate = endDate;
        break;
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = yesterday.toISOString().split('T')[0];
        endDate = startDate;
        break;
      }
      case 'thisWeek': {
        const thisMonday = new Date(today);
        const day = thisMonday.getDay();
        const diff = thisMonday.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
        thisMonday.setDate(diff);
        startDate = thisMonday.toISOString().split('T')[0];
        break;
      }
      case 'lastWeek': {
        const lastMonday = new Date(today);
        const day = lastMonday.getDay();
        lastMonday.setDate(lastMonday.getDate() - day - 6); // Last Monday
        startDate = lastMonday.toISOString().split('T')[0];

        const lastSunday = new Date(lastMonday);
        lastSunday.setDate(lastSunday.getDate() + 6);
        endDate = lastSunday.toISOString().split('T')[0];
        break;
      }
      case 'thisMonth': {
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = firstOfMonth.toISOString().split('T')[0];
        break;
      }
      case 'lastMonth': {
        const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        startDate = firstOfLastMonth.toISOString().split('T')[0];

        const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        endDate = lastOfLastMonth.toISOString().split('T')[0];
        break;
      }
      default:
        break;
    }

    setLocalDateRange({ startDate, endDate });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const [year, month, day] = dateStr.split('-');
      const dateObj = new Date(`${year}-${month}-${day}T12:00:00`);
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      return dateObj.toLocaleDateString('en-US', options);
    } catch (e) {
      return dateStr;
    }
  };

  const displayValue = value.startDate || value.endDate
    ? `${value.startDate ? formatDate(value.startDate) : 'Any'} - ${value.endDate ? formatDate(value.endDate) : 'Any'}`
    : 'Any date';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center p-3 h-12 rounded border bg-green-50 text-green-700"
      >
        <Calendar size={16} className="text-green-700" />
        <span className="ml-2 text-sm">{label}: {displayValue}</span>
        <ChevronDown size={16} className="ml-2 text-green-700" />
      </button>

      {isOpen && (
        <div className="absolute mt-1 bg-white border rounded shadow-lg z-10 w-72 p-3">
          {/* Quick select options */}
          <div className="mb-3 border-b pb-2">
            <label className="block text-sm font-medium mb-2">Quick Select:</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleQuickSelect('today')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700"
              >
                Today
              </button>
              <button
                onClick={() => handleQuickSelect('yesterday')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700"
              >
                Yesterday
              </button>
              <button
                onClick={() => handleQuickSelect('thisWeek')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700"
              >
                This Week
              </button>
              <button
                onClick={() => handleQuickSelect('lastWeek')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700"
              >
                Last Week
              </button>
              <button
                onClick={() => handleQuickSelect('thisMonth')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700"
              >
                This Month
              </button>
              <button
                onClick={() => handleQuickSelect('lastMonth')}
                className="p-2 text-xs bg-green-50 hover:bg-green-100 rounded text-green-700"
              >
                Last Month
              </button>
            </div>
          </div>

          {/* Custom date range */}
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">Start Date:</label>
            <input
              type="date"
              value={localDateRange.startDate}
              onChange={(e) => setLocalDateRange({ ...localDateRange, startDate: e.target.value })}
              className="w-full p-2 border rounded h-10"
            />
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">End Date:</label>
            <input
              type="date"
              value={localDateRange.endDate}
              onChange={(e) => setLocalDateRange({ ...localDateRange, endDate: e.target.value })}
              className="w-full p-2 border rounded h-10"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              onClick={() => setLocalDateRange({ startDate: '', endDate: '' })}
              className="text-xs text-red-600 hover:underline p-2"
            >
              Clear
            </button>
            <button
              onClick={() => {
                onChange(localDateRange);
                setIsOpen(false);
              }}
              className="px-4 py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple dialog component
const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        <h3 className="text-lg font-medium mb-4">{message}</h3>
        <div className="flex justify-end gap-4">
          {isConfirmation ? (
            <>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={onClose}
              >
                Yes
              </button>
              <button
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={onCancel || onClose}
              >
                No
              </button>
            </>
          ) : (
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={onClose}
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  let bgColor = "bg-gray-100";
  let textColor = "text-gray-800";

  if (status === "Rejected") {
    bgColor = "bg-red-100";
    textColor = "text-red-800";
  } else if (status === "Approved") {
    bgColor = "bg-green-100";
    textColor = "text-green-800";
  } else if (status === "In Progress" || status === "Approval requested") {
    bgColor = "bg-blue-100";
    textColor = "text-blue-800";
  } else if (status === "Pending") {
    bgColor = "bg-yellow-100";
    textColor = "text-yellow-800";
  }

  return (
    <span className={`px-2 py-1 rounded-full text-xs ${bgColor} ${textColor}`}>
      {status}
    </span>
  );
};

// Priority badge component
const PriorityBadge = ({ priority }) => {
  let bgColor = "bg-gray-100";
  let textColor = "text-gray-800";

  if (priority === "High") {
    bgColor = "bg-red-100";
    textColor = "text-red-800";
  } else if (priority === "Medium") {
    bgColor = "bg-yellow-100";
    textColor = "text-yellow-800";
  } else if (priority === "Low") {
    bgColor = "bg-green-100";
    textColor = "text-green-800";
  }

  return (
    <span className={`px-2 py-1 rounded-full text-xs ${bgColor} ${textColor}`}>
      {priority}
    </span>
  );
};

// Update Status Dialog component
const UpdateStatusDialog = ({ isOpen, onClose, onSubmit, selectedIds, allStatuses }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [error, setError] = useState('');
  const statusInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus('');
      setError('');
    }
  }, [isOpen]);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && statusInputRef.current) {
      statusInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedStatus) {
      setError('Status updated successfully');

      // Delay the update to show success message
      setTimeout(() => {
        onSubmit(selectedStatus);
      }, 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, width: '500px'}}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF', textAlign: 'center' }}>
          Update Requirement Status
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>New Status*:</label>
            <select
              ref={statusInputRef}
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            >
              <option value="">Select a status</option>
              {allStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          {error && (
            <div className={`mb-4 p-2 rounded ${
              error.includes('successfully')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-500'
            }`}>
              {error}
            </div>
          )}
          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={!selectedStatus}
            >
              Update Status
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Update Criticality Dialog component
const UpdateCriticalityDialog = ({ isOpen, onClose, onSubmit, selectedIds, allCriticalities }) => {
  const [selectedCriticality, setSelectedCriticality] = useState('');
  const [error, setError] = useState('');
  const criticalityInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedCriticality('');
      setError('');
    }
  }, [isOpen]);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && criticalityInputRef.current) {
      criticalityInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedCriticality) {
      setError('Criticality updated successfully');

      // Delay the update to show success message
      setTimeout(() => {
        onSubmit(selectedCriticality);
      }, 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, width: '500px'}}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' , textAlign: 'center'}}>
          Update Requirement Criticality
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>New Criticality*:</label>
            <select
              ref={criticalityInputRef}
              value={selectedCriticality}
              onChange={(e) => setSelectedCriticality(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            >
              <option value="">Select a criticality level</option>
              {allCriticalities.map(criticality => (
                <option key={criticality} value={criticality}>{criticality}</option>
              ))}
            </select>
          </div>
          {error && (
            <div className={`mb-4 p-2 rounded ${
              error.includes('successfully')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-500'
            }`}>
              {error}
            </div>
          )}
          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={!selectedCriticality}
            >
              Update Criticality
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Update Priority Dialog component
const UpdatePriorityDialog = ({ isOpen, onClose, onSubmit, selectedIds, allPriorities }) => {
  const [selectedPriority, setSelectedPriority] = useState('');
  const [error, setError] = useState('');
  const priorityInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedPriority('');
      setError('');
    }
  }, [isOpen]);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && priorityInputRef.current) {
      priorityInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedPriority) {
      setError('Priority updated successfully');

      // Delay the update to show success message
      setTimeout(() => {
        onSubmit(selectedPriority);
      }, 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{...DialogStyles.dialog, width: '500px'}}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF', textAlign: 'center' }}>
          Update Requirement Priority
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px'}}>New Priority*:</label>
            <select
              ref={priorityInputRef}
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            >
              <option value="">Select a priority level</option>
              {allPriorities.map(priority => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
          </div>
          {error && (
            <div className={`mb-4 p-2 rounded ${
              error.includes('successfully')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-500'
            }`}>
              {error}
            </div>
          )}
          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={!selectedPriority}
            >
              Update Priority
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

























  // Initial load of user info
  // Initial load of user info
useEffect(() => {
  console.log('Inside useEffect for initial load');

  const setInitialLoad = async () => {
    const current_project = localStorage.getItem('current_project');
    if (!login_token) {
      showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
      return;
    }

    try {
      const result = await getUserInfo({
        login_token: login_token
      });

      if (result.success) {
        if (result.userInfo.default_project === 'null' && current_project === 'null') {
          showErrorDialog('Please select a project on the home page', '/RequirementsPortal');
          return;
        }

        const project = current_project === 'null' ? result.userInfo.default_project : current_project;

        // Set the states
        setCorporateAccount(result.userInfo.corporate_account);
        setCurrentProject(project);
        setUserId(result.user_id);
        setUserName(result.userInfo.user_name);
        setAccountDescription(result.userInfo.account_description);

        console.log('User info:', result.userInfo);
        console.log('Corporate Account set to:', result.userInfo.corporate_account);

        // We will not set the approver filter here - we'll let the fetchApproverList function do it
        // after it loads the data from the API

      } else {
        showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
      }
    } catch (error) {
      console.error('Error calling API:', error);
    }
  };

  setInitialLoad();
}, []);

  // Add useEffect to fetch data when corporateAccount and currentProject are set
// Update the useEffect to include sortConfig in dependencies
useEffect(() => {
  if (corporateAccount && currentProject) {
    fetchRequirements();
      setCurrentPage(1);
  }
}, [
  corporateAccount,
  currentProject,
  statusFilter,
  approverFilter,
  criticalityFilter,
  priorityFilter,
  searchFilter,
  createdDateFilter,
  updatedDateFilter,
  sortConfig,
    approvalStatusFilter,
  approvalDateFilter
]);

  // More code to be added in the next parts...







return (
  <div className="min-h-screen bg-gray-50 p-6">
    <Header />

    <div className="mt-10">
      {/* Title bar */}

 <h1 className="text-l font-bold text-blue-800 mr-20 text-center">My Requirement Approvals</h1>
<div className="flex items-center mb-4 mt-3 justify-between bg-blue-100 p-3 rounded">
  {/* Search bar */}
  <div className="flex items-center">
    <input
      type="text"
      value={searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
          // Reload table when search is cleared
          if (e.target.value === '') {
            setSearchFilter('');
          }
        }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleSearch();
        }
      }}
      placeholder="Search requirements..."
      className="p-2 pr-10 border rounded w-full"
    />
    <button
      onClick={handleSearch}
      className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
    >
      <Search size={18} />
    </button>
  </div>



  <h2 className="text-l font-bold text-blue-700 text-center flex-1">Project: {currentProject}</h2>

</div>







{/* Filters section */}
<div className={`mb-4 bg-white rounded-lg shadow transition-all duration-300 ${showFilters ? 'p-4' : 'p-2'}`}>






 <div className="flex items-center justify-between mb-2">
    {/* Toggle button for Filters */}
    <button
      onClick={() => setShowFilters(!showFilters)}
      className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
    >
      {showFilters ? <EyeOff size={16} /> : <Eye size={16} />}
      <span className="ml-2 font-medium">
        {showFilters ? 'Hide' : 'Show'} Filters
      </span>
      {showFilters ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
    </button>

    {/* When collapsed, show active filter count */}
    {!showFilters && (
      <div className="text-sm text-gray-600">
        {[
          statusFilter.length > 0 && `${statusFilter.length} status`,
          approverFilter.length > 0 && `${approverFilter.length} approver`,
          criticalityFilter.length > 0 && `${criticalityFilter.length} criticality`,
          priorityFilter.length > 0 && `${priorityFilter.length} priority`,
          approvalStatusFilter.length > 0 && `${approvalStatusFilter.length} approval status`,
          (createdDateFilter.startDate || createdDateFilter.endDate) && 'creation date',
          (updatedDateFilter.startDate || updatedDateFilter.endDate) && 'update date',
          (approvalDateFilter.startDate || approvalDateFilter.endDate) && 'approval date'
        ].filter(Boolean).join(', ') || 'No active filters'}
      </div>
    )}
  </div>






  {/* Collapsible filters content */}
  {showFilters && (
    <>


  <div className="flex flex-wrap gap-3 mb-3">
    <div className="flex items-center">
      <Filter size={24} className="text-gray-600 mr-2" />
      <span className="text-sm font-medium text-gray-700"></span>
    </div>

    {/* Status Filter Dropdown */}
    <div className="relative" style={{ minWidth: '160px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-yellow-50 text-yellow-700"
        onClick={() => {
          const dropdown = document.getElementById('status-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-status-7410179-6e5333.svg" alt="Status Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Status ({statusFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="status-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setStatusFilter([...allStatuses]);
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setStatusFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {allStatuses.map(status => (
          <div
            key={status}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setStatusFilter(prev => {
                if (prev.includes(status)) {
                  return prev.filter(s => s !== status);
                } else {
                  return [...prev, status];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={statusFilter.includes(status)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{status}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Approver Filter Dropdown */}
    <div className="relative" style={{ minWidth: '180px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-blue-50 text-blue-700"
        onClick={() => {
          const dropdown = document.getElementById('approver-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-person-7574717-6e5333.svg" alt="Approver Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Approver ({approverFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="approver-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                // Use allApprovers for complete list
                setApproverFilter(allApprovers.map(approver => approver.approval_user_id));
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setApproverFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {allApprovers.map(approver => (
          <div
            key={approver.approval_user_id}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setApproverFilter(prev => {
                if (prev.includes(approver.approval_user_id)) {
                  return prev.filter(id => id !== approver.approval_user_id);
                } else {
                  return [...prev, approver.approval_user_id];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={approverFilter.includes(approver.approval_user_id)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{approver.user_name} ({approver.approval_user_id})</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Criticality Filter Dropdown */}
    <div className="relative" style={{ minWidth: '180px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-pink-50 text-pink-700"
        onClick={() => {
          const dropdown = document.getElementById('criticality-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-critical-status-7295245-6e5333.svg" alt="Criticality Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Criticality ({criticalityFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="criticality-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setCriticalityFilter([...allCriticalities]);
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setCriticalityFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {allCriticalities.map(criticality => (
          <div
            key={criticality}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setCriticalityFilter(prev => {
                if (prev.includes(criticality)) {
                  return prev.filter(c => c !== criticality);
                } else {
                  return [...prev, criticality];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={criticalityFilter.includes(criticality)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{criticality}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Priority Filter Dropdown */}
    <div className="relative" style={{ minWidth: '160px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-blue-50 text-blue-700"
        onClick={() => {
          const dropdown = document.getElementById('priority-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-priority-7620160-6e5333.svg" alt="Priority Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Priority ({priorityFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="priority-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setPriorityFilter([...allPriorities]);
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setPriorityFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {allPriorities.map(priority => (
          <div
            key={priority}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setPriorityFilter(prev => {
                if (prev.includes(priority)) {
                  return prev.filter(p => p !== priority);
                } else {
                  return [...prev, priority];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={priorityFilter.includes(priority)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{priority}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Approval Status Filter Dropdown */}
    <div className="relative" style={{ minWidth: '180px' }}>
      <div
        className="p-2 border rounded flex justify-between items-center cursor-pointer bg-pink-50 text-pink-700"
        onClick={() => {
          const dropdown = document.getElementById('approval-status-filter-dropdown');
          if (dropdown) {
            dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
          }
        }}
      >
        <div className="flex items-center">
          <img src="/icons/noun-status-7410179-6e5333.svg" alt="Approval Status Icon" className="w-6 h-6 mr-2" />
          <span className="text-sm">
            Approval Sts ({approvalStatusFilter.length})
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <div
        id="approval-status-filter-dropdown"
        className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
        style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="p-2 border-b sticky top-0 bg-white">
          <div className="flex justify-between items-center">
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setApprovalStatusFilter([...allApprovalStatuses]);
              }}
            >
              Select All
            </button>
            <button
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                setApprovalStatusFilter([]);
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {allApprovalStatuses.map(approvalStatus => (
          <div
            key={approvalStatus}
            className="p-2 hover:bg-gray-100 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setApprovalStatusFilter(prev => {
                if (prev.includes(approvalStatus)) {
                  return prev.filter(s => s !== approvalStatus);
                } else {
                  return [...prev, approvalStatus];
                }
              });
            }}
          >
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={approvalStatusFilter.includes(approvalStatus)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4"
              />
              <span>{approvalStatus}</span>
            </label>
          </div>
        ))}
      </div>
    </div>

    {/* Date filters */}
    <div className="float-right flex items-center ml-4 gap-5 h-15">
      {/* Created Date filter */}
      <DateRangePicker
        label="Created Dt"
        value={createdDateFilter}
        onChange={setCreatedDateFilter}
        icon={<Calendar size={16} />}
      />

      {/* Updated Date filter */}
      <DateRangePicker
        label="Updated Dt"
        value={updatedDateFilter}
        onChange={setUpdatedDateFilter}
        icon={<Calendar size={16} />}
      />

      {/* Approval Date filter */}
      <DateRangePicker
        label="Approval Dt"
        value={approvalDateFilter}
        onChange={setApprovalDateFilter}
        icon={<Calendar size={16} />}
      />

      {/* Reset filters button */}
      <button
        onClick={() => {
          setStatusFilter([]);
          setApproverFilter([]);
          setCriticalityFilter([]);
          setPriorityFilter([]);
          setCreatedDateFilter({ startDate: '', endDate: '' });
          setUpdatedDateFilter({ startDate: '', endDate: '' });
          setApprovalStatusFilter([]);
          setApprovalDateFilter({ startDate: '', endDate: '' });
          setSearchFilter('');
          setSearchInput('');
        }}
        className="ml-auto px-3 py-1 text-lg bg-red-300 text-white rounded hover:bg-red-500 flex items-center"
      >
        <X size={18} className="mr-1" />
        Reset
      </button>
    </div>
  </div>



    </>
  )}







</div>















      {/* Main content and sidebar in a flex container */}
      <div className="flex gap-6">







        {/* Main content area */}
        <div className="flex-1">
          {/* Main Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              </div>
            ) : requirements.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No requirements found matching your filters.
              </div>
            ) : (
              <div className="overflow-x-auto">







   {/* Updated Table with Combined Approval Status and Comments Column */}
<table className="min-w-full divide-y divide-gray-800">
  <thead className="bg-gray-200">
    <tr>
      <th scope="col" className="px-4 py-3 text-center w-10">


<input
  type="checkbox"
  checked={itemsPerPage > 0 && requirements.slice(startIndex, startIndex + itemsPerPage).every(req => selectedRequirements.has(req.req_id))}
  onChange={(e) => {
    if (e.target.checked) {
      // Select all visible requirements
      const newSelected = new Set(selectedRequirements);
      requirements.slice(startIndex, startIndex + itemsPerPage).forEach(req => newSelected.add(req.req_id));
      setSelectedRequirements(newSelected);
    } else {
      // Deselect all visible requirements
      const newSelected = new Set(selectedRequirements);
      requirements.slice(startIndex, startIndex + itemsPerPage).forEach(req => newSelected.delete(req.req_id));
      setSelectedRequirements(newSelected);
    }
  }}
  className="w-4 h-4"
/>



      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('req_id')}
      >
        <div className="flex items-center">
          <span className="underline">ID</span>
          {sortConfig.key === 'req_id' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>


              <th
          scope="col"
          className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
          onClick={() => handleSort('req_type')}
        >
          <div className="flex items-center">
            <span className="underline">APPROVAL LEVEL</span>
            {sortConfig.key === 'req_type' && (
              <span className="ml-1">
                {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
              </span>
            )}
          </div>
        </th>







      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('level_description')}
      >
        <div className="flex items-center">
          <span className="underline">Functional Level</span>
          {sortConfig.key === 'level_description' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('req_description')}
      >
        <div className="flex items-center">
          <span className="underline">Requirement Description</span>
          {sortConfig.key === 'req_description' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('c.status')}
      >
        <div className="flex items-center">
          <span className="underline">Req Status</span>
          {sortConfig.key === 'req_status' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('req_criticality')}
      >
        <div className="flex items-center">
          <span className="underline">Req Criticality</span>
          {sortConfig.key === 'req_criticality' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('req_priority')}
      >
        <div className="flex items-center">
          <span className="underline">Req Priority</span>
          {sortConfig.key === 'req_priority' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('user_name')}
      >
        <div className="flex items-center">
          <span className="underline">Approver</span>
          {sortConfig.key === 'approver_user_name' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('approval_status')}
      >
        <div className="flex items-center">
          <span className="underline">Approval Status</span>
          {sortConfig.key === 'approval_status' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('approval_date')}
      >
        <div className="flex items-center">
          <span className="underline">Approval Date</span>
          {sortConfig.key === 'approval_date' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        onClick={() => handleSort('number_of_approvers')}
      >
        <div className="flex items-center">
          <span className="underline">No. of Approvers</span>
          {sortConfig.key === 'number_of_approvers' && (
            <span className="ml-1">
              {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
            </span>
          )}
        </div>
      </th>
    </tr>
  </thead>
  <tbody className="bg-white divide-y divide-gray-200">
    {requirements.slice(startIndex, startIndex + itemsPerPage).map((req) => (
        <tr
          key={req.REQUIREMENT_APPROVER_RECORD_ID}
          className="border-t hover:bg-gray-50"
          style={{
            backgroundColor: selectedRequirements.has(req.REQUIREMENT_APPROVER_RECORD_ID) ? '#DEEFF5' : 'transparent'
          }}
        >
        <td className="px-4 py-3 text-center">
          <input
            type="checkbox"
            checked={selectedRequirements.has(req.REQUIREMENT_APPROVER_RECORD_ID)}
            onChange={() => {
              const newSelected = new Set(selectedRequirements);
              if (newSelected.has(req.REQUIREMENT_APPROVER_RECORD_ID)) {
                newSelected.delete(req.REQUIREMENT_APPROVER_RECORD_ID);
              } else {
                newSelected.add(req.REQUIREMENT_APPROVER_RECORD_ID);
              }
              setSelectedRequirements(newSelected);
            }}
            className="w-4 h-4"
          />
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="text-blue-600 hover:text-blue-800 hover:underline">
            {req.req_id_with_prefix}
          </span>
        </td>



        <td className="px-4 py-3 whitespace-nowrap">
          <RequirementTypeBadge reqType={req.req_type} />
        </td>



        <td className="px-4 py-3 whitespace-nowrap">
          <span className="text-sm">{req.level_description}</span>
        </td>
        <td className="px-4 py-3">
          <div className="text-sm text-gray-900 max-w-md truncate">{req.req_description}</div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <StatusBadge status={req.req_status} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="text-xs">{req.req_criticality}</span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <PriorityBadge priority={req.req_priority} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="text-xs">{req.approver_user_name}</span>
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-col">
            <span className="text-xs mb-1">
              <StatusBadge status={req.approval_status || 'Pending'} />
            </span>
            {(req.approver_comments) && (
              <span className="text-xs text-blue-600 italic mt-1 max-w-xs">
                "{req.approver_comments}"
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="text-xs">
            {req.approval_date ? new Date(req.approval_date).toLocaleDateString() : '-'}
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-xs">
            {req.number_of_approvers || req.no_of_approvers || '0'}
            {req.approver_name &&
              <span className="text-xs ml-1 text-gray-500">({req.approver_name})</span>
            }
          </div>
        </td>
      </tr>
    ))}
  </tbody>
</table>
















              </div>
            )}
          </div>

          {/* Pagination controls */}
          {!loading && requirements.length > 0 && (
            <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Total Requirements: {requirements.length}</span>
                <span>| Showing {requirements.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, requirements.length)} of {requirements.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, Math.ceil(requirements.length / itemsPerPage))}</span>
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(requirements.length / itemsPerPage)))}
                  disabled={currentPage === Math.ceil(requirements.length / itemsPerPage) || requirements.length === 0}
                  aria-label="Next page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  className="border rounded p-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar with buttons and icons */}
        <div className="w-64 bg-white border rounded shadow-sm self-start sticky top-6">
          <div className="p-4">


            {/* Approval management */}
            <h4 className="font-bold text-xs text-purple-600 bg-purple-200 p-2 mb-2 rounded">APPROVAL MANAGEMENT</h4>
            <div className="space-y-2 mb-6">
              <button
                className={`w-full px-3 py-2 rounded text-sm flex items-center text-purple-700 hover:text-purple-900 `}
                onClick={handleManageApproversClick}
              >
                <img
                  src="/icons/noun-team-6990022-644ebd.svg"
                  alt="Manage Approvers"
                  className={`w-8 h-8 mr-3 `}
                />
                <span className={`flex items-center text-purple-700 `}>
                  Manage Approvers

                </span>
              </button>

              <button
                className={`w-full px-3 py-2 rounded text-sm flex items-center text-purple-700 hover:text-purple-900 ${
                  !permissions.canApprove ? 'text-gray-400' : 'text-purple-700 hover:text-purple-900'
                }`}
                disabled={!permissions.canApprove}
                onClick={handleApproveButtonClick}
              >
                <img
                  src="/icons/noun-endorse-6109251-4e26ee.svg"
                  alt="Approve Requirements"
                  className={`w-8 h-8 mr-3 ${!permissions.canApprove ? 'opacity-50' : ''}`}
                />
                <span className={`flex items-center ${!permissions.canApprove ? 'text-purple-400' : 'text-purple-700'}`}>
                  Approve Requirements
                  <img
                    src="/icons/noun-one-to-many-1406451-644ebd.svg"
                    alt="Info"
                    className={`w-8 h-8 ml-2 ${!permissions.canApprove ? 'opacity-50' : ''}`}
                  />
                </span>
              </button>
            </div>


            <div className="flex items-center text-xs text-gray-500 italic mt-4">
              <img
                src="/icons/noun-one-to-many-1406451-676767.svg"
                alt="Info"
                className="w-6 h-6 ml-2"
              />
              -Supports multiple row selection on requirements only
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Error dialog */}
    {showDialog && (
      <Dialog
        message={dialogMessage}
        onClose={() => setShowDialog(false)}
      />
    )}

  {/* Status dialog */}

<UpdateStatusDialog
  isOpen={isUpdateStatusDialogOpen}
  onClose={() => setIsUpdateStatusDialogOpen(false)}
  onSubmit={handleUpdateStatus}
  selectedIds={Array.from(selectedRequirements).map(recordId => {
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
    return selectedReq ? selectedReq.req_id : null;
  }).filter(Boolean)}
  allStatuses={allStatuses}
/>




{/* Criticality dialog */}
<UpdateCriticalityDialog
  isOpen={isUpdateCriticalityDialogOpen}
  onClose={() => setIsUpdateCriticalityDialogOpen(false)}
  onSubmit={handleUpdateCriticality}
  selectedIds={Array.from(selectedRequirements).map(recordId => {
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
    return selectedReq ? selectedReq.req_id : null;
  }).filter(Boolean)}
  allCriticalities={allCriticalities}
/>

{/* Priority dialog */}
<UpdatePriorityDialog
  isOpen={isUpdatePriorityDialogOpen}
  onClose={() => setIsUpdatePriorityDialogOpen(false)}
  onSubmit={handleUpdatePriority}
  selectedIds={Array.from(selectedRequirements).map(recordId => {
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
    return selectedReq ? selectedReq.req_id : null;
  }).filter(Boolean)}
  allPriorities={allPriorities}
/>

{/* Copy dialog */}
<CopyRequirementDialog
  isOpen={isCopyDialogOpen}
  onClose={(success) => {
    setIsCopyDialogOpen(false);
    if (success) {
      // Refresh the requirements list after successful copy
      fetchRequirements();
      setSelectedRequirements(new Set());
      setDialogMessage('Requirement copied successfully');
      setShowDialog(true);
    }
  }}
  requirementToCopy={selectedRequirementForCopy}
  corporateAccount={corporateAccount}
  currentProject={currentProject}
  login_token={login_token}
/>

{/* Classification dialog */}
<UpdateClassificationDialog
  isOpen={isClassificationDialogOpen}
  onClose={() => setIsClassificationDialogOpen(false)}
  selectedRequirements={new Set(
    Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean)
  )}
  corporateAccount={corporateAccount}
  currentProject={currentProject}
  login_token={login_token}
  requirementIdWithPrefix={
    selectedRequirements.size === 1
      ? (() => {
          const selectedRecordId = Array.from(selectedRequirements)[0];
          const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
          return selectedReq ? selectedReq.req_id_with_prefix : '';
        })()
      : ''
  }
/>

{/* Create Exception dialog */}
<CreateExceptionDialog
  isOpen={isCreateExceptionDialogOpen}
  onClose={() => setIsCreateExceptionDialogOpen(false)}
  selectedRequirements={new Set(
    Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean)
  )}
  corporateAccount={corporateAccount}
  currentProject={currentProject}
  login_token={login_token}
  functionalLevelDescription={getCurrentFunctionalLevelDescription()}
  requirementIdWithPrefix={
    selectedRequirements.size === 1
      ? (() => {
          const selectedRecordId = Array.from(selectedRequirements)[0];
          const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
          return selectedReq ? selectedReq.req_id_with_prefix : '';
        })()
      : ''
  }
/>

{/* Manage Exceptions dialog */}
<ManageRequirementExceptions
  isOpen={isKeyAttributesDialogOpen}
  onClose={() => setIsKeyAttributesDialogOpen(false)}
  selectedIds={Array.from(selectedRequirements).map(recordId => {
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
    return selectedReq ? selectedReq.req_id : null;
  }).filter(Boolean)}
  corporateAccount={corporateAccount}
  currentProject={currentProject}
  login_token={login_token}
  canEdit={permissions.canManageExceptions}
  exceptionLevel={exceptionLevel}
  requirementIdWithPrefix={
    selectedRequirements.size === 1
      ? (() => {
          const selectedRecordId = Array.from(selectedRequirements)[0];
          const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
          return selectedReq ? selectedReq.req_id_with_prefix : '';
        })()
      : ''
  }
/>

{/* Manage Approvers dialog */}
<ManageApproversDialog
  isOpen={isManageApproversDialogOpen}
  onClose={(success) => {
    setIsManageApproversDialogOpen(false);
    if (success) {
      // Refresh the requirements list after successful changes
      fetchRequirements();
      setSelectedRequirements(new Set());
    }
  }}
  selectedRequirements={new Set(
    Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean)
  )}
  selectedLevelId={selectedRequirements.size > 0 ? (() => {
    const selectedRecordId = Array.from(selectedRequirements)[0];
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
    return selectedReq ? selectedReq.level_id : null;
  })() : null}
  functionalLevelDescription={selectedRequirements.size > 0 ? (() => {
    const selectedRecordId = Array.from(selectedRequirements)[0];
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
    return selectedReq ? selectedReq.level_description : null;
  })() : null}
  corporateAccount={corporateAccount}
  currentProject={currentProject}
  login_token={login_token}
  canEdit={permissions.canManageApprovers}
  requirementIdWithPrefix={
    selectedRequirements.size === 1
      ? (() => {
          const selectedRecordId = Array.from(selectedRequirements)[0];
          const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
          return selectedReq ? selectedReq.req_id_with_prefix : '';
        })()
      : ''
  }
    // Pass the req_type from the selected record
  req_type={selectedRequirements.size > 0 ? (() => {
    const selectedRecordId = Array.from(selectedRequirements)[0];
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
    return selectedReq ? selectedReq.req_type : 'PROJECT LEVEL';
  })() : 'PROJECT LEVEL'}
/>


{/* Manage Use Cases dialog */}
<ManageUseCasesDialog
  isOpen={isManageUseCasesDialogOpen}
  onClose={() => setIsManageUseCasesDialogOpen(false)}
  selectedRequirements={new Set(
    Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean)
  )}
  corporateAccount={corporateAccount}
  currentProject={currentProject}
  login_token={login_token}
  requirementIdWithPrefix={
    selectedRequirements.size === 1
      ? (() => {
          const selectedRecordId = Array.from(selectedRequirements)[0];
          const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
          return selectedReq ? selectedReq.req_id_with_prefix : '';
        })()
      : ''
  }
/>

{/* Manage Comments dialog */}
<ManageCommentsDialog
  isOpen={isManageCommentsDialogOpen}
  onClose={() => setIsManageCommentsDialogOpen(false)}
  selectedRequirements={new Set(
    Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean)
  )}
  corporateAccount={corporateAccount}
  currentProject={currentProject}
  login_token={login_token}
  requirementIdWithPrefix={
    selectedRequirements.size === 1
      ? (() => {
          const selectedRecordId = Array.from(selectedRequirements)[0];
          const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
          return selectedReq ? selectedReq.req_id_with_prefix : '';
        })()
      : ''
  }
/>

{/* Manage Links dialog */}
<ManageLinksDialog
  isOpen={isManageLinksDialogOpen}
  onClose={() => setIsManageLinksDialogOpen(false)}
  corporateAccount={corporateAccount}
  projectId={currentProject}
  login_token={login_token}
  sourceRecordType="REQUIREMENT"
  selectedSourceIds={new Set(
    Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean)
  )}
  sourceIdWithPrefix={
    selectedRequirements.size === 1
      ? (() => {
          const selectedRecordId = Array.from(selectedRequirements)[0];
          const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
          return selectedReq ? selectedReq.req_id_with_prefix : '';
        })()
      : ''
  }
/>


{/* Approve Requirements dialog */}
<ApproveRequirementsDialog
  isOpen={isApprovalDialogOpen}
  onClose={(success, refreshOnly = false) => {
    // If refreshOnly is true, just refresh the list but keep dialog open
    if (refreshOnly) {
      if (success) {
        // Refresh the requirements list but don't close the dialog
        fetchRequirements();
        setSelectedRequirements(new Set());
      }
      return; // Don't close the dialog
    }

    // Normal close behavior
    setIsApprovalDialogOpen(false);
    if (success) {
      // Refresh the requirements list after successful approval changes
      fetchRequirements();
      setSelectedRequirements(new Set());
//      setDialogMessage('Requirements approval status updated successfully');
//      setShowDialog(true);
    }
  }}
  selectedRequirements={new Set(
    Array.from(selectedRequirements).map(recordId => {
      const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === recordId);
      return selectedReq ? selectedReq.req_id : null;
    }).filter(Boolean)
  )}

    selectedLevelId={selectedRequirements.size > 0 ? (() => {
    const selectedRecordId = Array.from(selectedRequirements)[0];
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
    return selectedReq ? selectedReq.level_id : null;
  })() : null}
  functionalLevelDescription={selectedRequirements.size > 0 ? (() => {
    const selectedRecordId = Array.from(selectedRequirements)[0];
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
    return selectedReq ? selectedReq.level_description : null;
  })() : null}

  corporateAccount={corporateAccount}
  currentProject={currentProject}
  login_token={login_token}
  canEdit={permissions.canApprove}
  requirementIdWithPrefix={
    // If only one item is selected, get its prefixed ID
    selectedRequirements.size === 1
      ? (() => {
          // Find the selected requirement
          const selectedRecordId = Array.from(selectedRequirements)[0];
          const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
          // Return its prefixed ID if found
          return selectedReq ? selectedReq.req_id_with_prefix : '';
        })()
      : '' // Empty string if multiple or no items selected
  }

  // Pass the req_type from the selected record
  req_type={selectedRequirements.size > 0 ? (() => {
    const selectedRecordId = Array.from(selectedRequirements)[0];
    const selectedReq = requirements.find(req => req.REQUIREMENT_APPROVER_RECORD_ID === selectedRecordId);
    return selectedReq ? selectedReq.req_type : 'PROJECT LEVEL';
  })() : 'PROJECT LEVEL'}


/>


  </div>
);

};

export default MyRequirementApprovals;